import type { GeneratedCeiling, GeneratedSubCeiling } from "building-types/ceilings/ceiling";
import type { DocEntityHandle, LevelHandle } from "building-types/handles/docEntityHandleTypes";
import type { SheetGeometry, SheetGeometryList } from "building-types/views/sheetGeometryTypes";
import { sheetAddGeometry, sheetPolylineGeometryNew } from "building-types/views/sheetGeometryTypes";
import colors from "common/constants/colorsOld";
import type { Table } from "common/data/table";
import type { SheetPoints } from "common/types/nominalNumbersTypes";
import { LW_EXTRA_THIN } from "doc/plans/constants";
import type { PlanGenerationContext } from "doc/plans/planGeometrySource";

export function makeSheetGeometryFromCeilings({ building, levelId }: PlanGenerationContext): SheetGeometry[] {
  const result: SheetGeometry[] = [];
  const { ceilings, ceilingsByLevel, levels } = building;

  function getCeilingsOnLevel(lvlId: string, handle: LevelHandle) {
    const out: SheetGeometry[] = [];
    for (const ceilingId of ceilingsByLevel[lvlId]) {
      const ceiling = ceilings[ceilingId];

      for (const subCeiling of ceiling.subCeilings) {
        const buildingEntity = subCeiling;

        const { outline, ceilingOutlineError } = subCeiling;

        if (ceilingOutlineError || !outline) {
          continue;
        }

        out.push(
          sheetPolylineGeometryNew({
            handle: subCeiling.handle ?? handle,
            paths: [outline],
            buildingEntity,
          }),
        );
      }
    }

    return out;
  }

  if (levelId) {
    const handle = levels[levelId].handle;
    result.push(...getCeilingsOnLevel(levelId, handle));
  } else {
    for (const lvl in levels) {
      const handle = levels[lvl].handle;
      result.push(...getCeilingsOnLevel(lvl, handle));
    }
  }

  return result;
}

interface CeilingByPredicateProps {
  handle: DocEntityHandle | null;
  ceilings: Table<GeneratedCeiling>;
  subCeilingPredicate: (ceiling: GeneratedSubCeiling) => boolean;
  color?: string;
  dashedLineworkLinewidth?: SheetPoints;
}

/** @deprecated This should be removed when cleaning up the duplicate roof annotation code */
export function planAddCeilingsByPredicate(
  sheetGeometryList: SheetGeometryList,
  {
    handle,
    ceilings,
    subCeilingPredicate,
    color = colors.black,
    dashedLineworkLinewidth = LW_EXTRA_THIN,
  }: CeilingByPredicateProps,
) {
  for (const ceilingId in ceilings) {
    const ceiling = ceilings[ceilingId];

    for (const subCeiling of ceiling.subCeilings) {
      if (!subCeilingPredicate(subCeiling)) {
        continue;
      }

      const { outline, ceilingOutlineError } = subCeiling;

      if (ceilingOutlineError || !outline) {
        continue;
      }

      sheetAddGeometry(
        sheetGeometryList,
        sheetPolylineGeometryNew({
          handle: subCeiling.handle ?? handle,
          paths: [outline],
          dash: true,
          linewidth: dashedLineworkLinewidth,
          strokeColor: color,
        }),
      );
    }
  }
}
